import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Badge, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { createPartner, updatePartner } from "../Api/PartnerMaster";
import { getVendorsByOrganisation } from "../Api/CustomerMaster";
import {
  createCusVenCp,
  fetchCusVenCpByCusVenType,
} from "../Api/CusVenCpMasterApi";

const PartnerModal = ({
  show,
  onHide,
  partner,
  orgId,
  projectId,
  onPartnerSaved,
}) => {
  const [formData, setFormData] = useState({
    partnerName: "",
    partnerEmailId: "",
    partnerPhoneNo: "",
    partnerRoles: [],
  });
  const [loading, setLoading] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  const [showCpModal, setShowCpModal] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [contactPersons, setContactPersons] = useState([]);

  const [cpForm, setCpForm] = useState({
    contactPerName: "",
    contactPerNumber: "",
    contactPerEmail: "",
  });

  // Available roles for selection
  const availableRoles = [
    "bill",
    "developer",
    "designer",
    "tester",
    "manager",
    "consultant",
  ];

  useEffect(() => {
    const fetchVendors = async () => {
      if (!orgId || !show) return;

      try {
        setLoadingVendors(true);
        const data = await getVendorsByOrganisation(parseInt(orgId));
        console.log("Fetched vendors:", data);
        setVendors(data || []);
      } catch (error) {
        toast.error("Failed to load vendors");
      } finally {
        setLoadingVendors(false);
      }
    };

    fetchVendors();
  }, [orgId, show]);

  useEffect(() => {
    const loadContacts = async () => {
      if (!formData.partnerName) return;

      try {
        setLoadingContacts(true);
        const data = await fetchCusVenCpByCusVenType(formData.partnerName);
        setContactPersons(data || []);
      } catch (err) {
        toast.error("Failed to load contact persons");
      } finally {
        setLoadingContacts(false);
      }
    };

    loadContacts();
  }, [formData.partnerName]);

  useEffect(() => {
    if (partner) {
      // Parse roles from JSON string if needed
      let roles = [];
      if (partner.partnerRole) {
        try {
          roles =
            typeof partner.partnerRole === "string"
              ? JSON.parse(partner.partnerRole)
              : partner.partnerRole;
        } catch (error) {
          console.error("Error parsing partner roles:", error);
          roles = [];
        }
      }

      setFormData({
        partnerName: partner.partnerName || "",
        partnerEmailId: partner.partnerEmailId || "",
        partnerPhoneNo: partner.partnerPhoneNo || "",
        partnerRoles: roles,
      });
    } else {
      setFormData({
        partnerName: "",
        partnerEmailId: "",
        partnerPhoneNo: "",
        partnerRoles: [],
      });
    }
    setNewRole("");
  }, [partner, show]);

  const openCpModal = () => {
    setCpForm({
      contactPerName: "",
      contactPerNumber: "",
      contactPerEmail: "",
    });
    setShowCpModal(true);
  };

  const closeCpModal = () => {
    setShowCpModal(false);
  };

  const handleCpChange = (e) => {
    setCpForm({ ...cpForm, [e.target.name]: e.target.value });
  };

  const handleSaveContactPerson = async () => {
    try {
      if (!formData.partnerName) return;

      const userDetails = JSON.parse(localStorage.getItem("userDetails"));

      const payload = {
        cusVenTypeId: formData.partnerName, // ✅ vendorCode
        contactPerName: cpForm.contactPerName,
        contactPerNumber: cpForm.contactPerNumber,
        contactPerEmail: cpForm.contactPerEmail,
        orgId: String(orgId),
        cusVenSourceTable: "vendor_master",
      };

      await createCusVenCp(payload, userDetails?.userId);

      toast.success("Contact person added successfully");
      closeCpModal();

      // 🔁 refresh list
      const updated = await fetchCusVenCpByCusVenType(formData.partnerName);
      setContactPersons(updated || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to add contact person");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddRole = () => {
    if (newRole.trim() && !formData.partnerRoles.includes(newRole.trim())) {
      setFormData((prev) => ({
        ...prev,
        partnerRoles: [...prev.partnerRoles, newRole.trim()],
      }));
      setNewRole("");
    }
  };

  const handleRemoveRole = (roleToRemove) => {
    setFormData((prev) => ({
      ...prev,
      partnerRoles: prev.partnerRoles.filter((role) => role !== roleToRemove),
    }));
  };

  const handleRoleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddRole();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.partnerName.trim()) {
      toast.error("Partner name is required");
      return;
    }

    setLoading(true);
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const userName = userDetails?.username || "system";

      const partnerData = {
        partnerName: formData.partnerName,
        // partnerEmailId: formData.partnerEmailId,
        // partnerPhoneNo: formData.partnerPhoneNo,
        partnerOrgId: String(orgId),
        partnerProject: projectId,
        // partnerRoles: formData.partnerRoles,
        createdBy: String(userDetails?.userId) || "system",
      };

      console.log("Sending partner data:", partnerData);
      console.log("Username:", userName);

      if (partner) {
        // Update existing partner
        partnerData.changedBy = String(userDetails?.userId) || "system";
        await updatePartner(partner.partnerId, partnerData, userName);
        toast.success("Partner updated successfully");
      } else {
        // Create new partner - pass userName as second parameter
        await createPartner(partnerData, userName);
        toast.success("Partner created successfully");
      }

      onPartnerSaved();
    } catch (error) {
      console.error("Error saving partner:", error);

      // More detailed error logging
      if (error.response) {
        console.error("Response error:", error.response.data);
        console.error("Response status:", error.response.status);
      }

      toast.error(error.message || "Failed to save partner");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {partner ? "Edit Partner" : "Add New Partner"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Partner Name *</Form.Label>
                  <Form.Select
                    value={formData.partnerName}
                    onChange={(e) =>
                      handleInputChange("partnerName", e.target.value)
                    }
                    disabled={loadingVendors}
                    required
                  >
                    <option value="">
                      {loadingVendors
                        ? "Loading vendors..."
                        : "Select Partner (Vendor)"}
                    </option>

                    {vendors.map((vendor) => (
                      <option key={vendor.vendorCode} value={vendor.vendorCode}>
                        {vendor.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              {/* <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.partnerEmailId}
                  onChange={(e) =>
                    handleInputChange("partnerEmailId", e.target.value)
                  }
                  placeholder="Enter email address"
                />
              </Form.Group>
            </Col> */}
            </Row>

            {formData.partnerName && (
              <div className="mb-3">
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={openCpModal}
                >
                  👤 Add Contact Person
                </Button>
              </div>
            )}

            {formData.partnerName && (
              <div className="mt-3">
                <h6>Contact Persons</h6>

                {loadingContacts ? (
                  <p>Loading contact persons...</p>
                ) : contactPersons.length > 0 ? (
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Number</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contactPersons.map((cp, i) => (
                        <tr key={cp.cusVenId}>
                          <td>{i + 1}</td>
                          <td>{cp.contactPerName}</td>
                          <td>{cp.contactPerNumber}</td>
                          <td>{cp.contactPerEmail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted">No contact persons found.</p>
                )}
              </div>
            )}

            {/* <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.partnerPhoneNo}
                  onChange={(e) =>
                    handleInputChange("partnerPhoneNo", e.target.value)
                  }
                  placeholder="Enter phone number"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Add Role</Form.Label>
                <div className="d-flex">
                  <Form.Control
                    type="text"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    onKeyPress={handleRoleKeyPress}
                    placeholder="Enter role or select from list"
                    list="availableRoles"
                  />
                  <datalist id="availableRoles">
                    {availableRoles.map((role) => (
                      <option key={role} value={role} />
                    ))}
                  </datalist>
                  <Button
                    variant="outline-secondary"
                    onClick={handleAddRole}
                    className="ms-2"
                  >
                    Add
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row> */}

            {/* Selected Roles */}
            {/* {formData.partnerRoles.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Selected Roles</Form.Label>
              <div>
                {formData.partnerRoles.map((role) => (
                  <Badge
                    key={role}
                    bg="primary"
                    className="me-2 mb-2 p-2"
                    style={{ fontSize: "0.9em" }}
                  >
                    {role}
                    <Button
                      variant="link"
                      className="text-white p-0 ms-2"
                      style={{ textDecoration: "none" }}
                      onClick={() => handleRemoveRole(role)}
                    >
                      ×
                    </Button>
                  </Badge>
                ))}
              </div>
            </Form.Group>
          )} */}

            {/* Quick Role Selection */}
            {/* <Form.Group className="mb-3">
            <Form.Label>Quick Select Roles</Form.Label>
            <div>
              {availableRoles.map((role) => (
                <Badge
                  key={role}
                  bg={
                    formData.partnerRoles.includes(role)
                      ? "success"
                      : "outline-secondary"
                  }
                  className="me-2 mb-2 p-2 cursor-pointer"
                  style={{ fontSize: "0.9em", cursor: "pointer" }}
                  onClick={() => {
                    if (formData.partnerRoles.includes(role)) {
                      handleRemoveRole(role);
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        partnerRoles: [...prev.partnerRoles, role],
                      }));
                    }
                  }}
                >
                  {role}
                </Badge>
              ))}
            </div>
          </Form.Group> */}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading
                ? "Saving..."
                : partner
                  ? "Update Partner"
                  : "Create Partner"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal show={showCpModal} onHide={closeCpModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Contact Person</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                name="contactPerName"
                value={cpForm.contactPerName}
                onChange={handleCpChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Number</Form.Label>
              <Form.Control
                name="contactPerNumber"
                value={cpForm.contactPerNumber}
                onChange={handleCpChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="contactPerEmail"
                value={cpForm.contactPerEmail}
                onChange={handleCpChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeCpModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveContactPerson}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PartnerModal;
